import { queryKeys } from "@/api/hooks";
import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import {
  useEnvironmentsQuery,
  type CompatStatus,
  type EnvironmentTenant,
} from "@/api/tenantRuntime";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/Badge";
import { Card } from "@/ui/Card";

const COMPAT = {
  pass: { label: "Pass", tone: "success" },
  fail: { label: "Fail", tone: "destructive" },
  unknown: { label: "Unknown", tone: "neutral" },
  not_running: { label: "Not running", tone: "outline" },
} satisfies Record<
  CompatStatus,
  { label: string; tone: "success" | "destructive" | "neutral" | "outline" }
>;

function EnvironmentCell({ entry }: { entry: EnvironmentTenant | undefined }) {
  if (entry === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  const compat = COMPAT[entry.compat.status];
  return (
    <div className="space-y-1 text-xs">
      <div className="font-mono">{entry.brainCommit?.slice(0, 8) ?? "—"}</div>
      <div>base {entry.baseRevision ?? "—"}</div>
      <div className="font-mono">
        {entry.overlay ?? "—"}@{entry.overlayRevision ?? "—"}
      </div>
      <Badge tone={compat.tone}>{compat.label}</Badge>
    </div>
  );
}

/**
 * The Projects page's Environments tab (`features.tenantTools`): one row per
 * tenant, one column per environment, read-only.
 */
export function EnvironmentsPanel() {
  const environments = useEnvironmentsQuery();

  if (environments.isPending) {
    return (
      <QueryPendingState
        isPaused={environments.isPaused}
        failureReason={environments.failureReason}
        label="Loading environments"
        errorTitle="Environments could not be loaded"
        rows={4}
        queryKey={queryKeys.environments}
      />
    );
  }
  if (environments.isError) {
    return (
      <QueryErrorState
        error={environments.error}
        subject="Environments"
        title="Environments could not be loaded"
        description="The Editor Service did not return the environment data."
        deniedDescription="You are not authorized to read environment data."
        onRetry={() => void environments.refetch()}
      />
    );
  }

  const { generatedAt, environments: columns } = environments.data;
  if (columns.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground">
        No environment data yet.
      </p>
    );
  }
  const tenants = [
    ...new Set(columns.flatMap((env) => env.tenants.map((t) => t.tenant))),
  ];

  return (
    <div className="space-y-3">
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[38rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs font-semibold text-muted-foreground">
              <th scope="col" className="px-3 py-2.5">
                Tenant
              </th>
              {columns.map((env) => (
                <th key={env.name} scope="col" className="px-3 py-2.5">
                  {env.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant} className="border-b border-border align-top">
                <td className="px-3 py-2 font-semibold" dir="auto">
                  {tenant}
                </td>
                {columns.map((env) => (
                  <td key={env.name} className="px-3 py-2">
                    <EnvironmentCell
                      entry={env.tenants.find((t) => t.tenant === tenant)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="text-xs text-muted-foreground">
        {generatedAt
          ? `Generated ${formatDateTime(generatedAt)}.`
          : "Generation time unknown."}
      </p>
    </div>
  );
}
